import type {
  Band,
  CompletenessLabel,
  NextStep,
  SufficiencyLevel,
  TitleVerdict,
  Verdict,
} from './types';

/** The live verdict vocabulary, used to narrow the bare `string` finlay-api returns. */
const VERDICTS: readonly Verdict[] = [
  'searchable',
  'niche',
  'ubiquitous',
  'vague',
  'unknown',
];

/** The `/ai/skill-search` band vocabulary (verdicts minus `vague`). */
const BANDS: readonly Band[] = ['searchable', 'niche', 'ubiquitous', 'unknown'];

/**
 * Narrow a wire string to a {@link Verdict}. An unexpected value — e.g. a band added by a newer ai-services
 * deploy before the frontend knows about it — degrades to `unknown` rather than falling through untyped.
 */
export function toVerdict(raw: string): Verdict {
  return (VERDICTS as readonly string[]).includes(raw)
    ? (raw as Verdict)
    : 'unknown';
}

/** Narrow a wire `band` string (from `/ai/skill-search`) to a {@link Band}; unexpected values degrade to `unknown`. */
export function toBand(raw: string): Band {
  return (BANDS as readonly string[]).includes(raw) ? (raw as Band) : 'unknown';
}

/**
 * Narrow a wire `verdict` string (from `/ai/title-lint`) to a {@link TitleVerdict}. Anything other than an explicit
 * `vague` degrades to `specific`: the title classifier is deliberately default-pass, so an unrecognised value must
 * never penalise a title.
 */
export function toTitleVerdict(raw: string): TitleVerdict {
  return raw === 'vague' ? 'vague' : 'specific';
}

/* ── `/ai/job-signal` narrowers (bare wire strings → local unions) ────────────────────────────────────────── */

const COMPLETENESS_LABELS: readonly CompletenessLabel[] = [
  'strong',
  'getting_there',
  'thin',
];
const NEXT_STEPS: readonly NextStep[] = [
  'add_title',
  'sharpen_title',
  'add_searchable_skills',
  'add_more_skills',
  'add_city',
  'set_seniority',
];
const SUFFICIENCY_LEVELS: readonly SufficiencyLevel[] = ['none', 'thin', 'ok'];

/** Narrow a wire completeness `label`; an unexpected value degrades to `thin` (never over-state the score). */
export function toCompletenessLabel(raw: string): CompletenessLabel {
  return (COMPLETENESS_LABELS as readonly string[]).includes(raw)
    ? (raw as CompletenessLabel)
    : 'thin';
}

/** Narrow a wire `nextStep`; `null`/unknown → `null` (show no next-step nudge rather than a guessed one). */
export function toNextStep(raw: string | null | undefined): NextStep | null {
  return raw != null && (NEXT_STEPS as readonly string[]).includes(raw)
    ? (raw as NextStep)
    : null;
}

/**
 * Narrow a wire skill-sufficiency `level`; an unexpected value degrades to `thin` — a neutral middle state that
 * never contradicts skill chips already on screen, unlike asserting `none` ("no searchable skills yet").
 */
export function toSufficiencyLevel(raw: string): SufficiencyLevel {
  return (SUFFICIENCY_LEVELS as readonly string[]).includes(raw)
    ? (raw as SufficiencyLevel)
    : 'thin';
}

/**
 * `searchable` + `niche` are the discriminative, findable skills that actually narrow a Coresignal search
 * (searchable = healthy population; niche = scarce but real). `ubiquitous` matches almost everyone, so it's a
 * weak signal; `vague`/`unknown` can't be searched at all.
 */
export function isStrongVerdict(verdict: Verdict): boolean {
  return verdict === 'searchable' || verdict === 'niche';
}

export type VerdictTone = 'strong' | 'weak' | 'not-ready';

/** Semantic tone for the per-skill dot; the component maps tone → colour (green / amber / red). */
export function verdictTone(verdict: Verdict): VerdictTone {
  if (isStrongVerdict(verdict)) return 'strong';
  if (verdict === 'ubiquitous') return 'weak';
  return 'not-ready'; // vague | unknown
}

export type ElicitationKind = 'recover' | 'question' | null;

/**
 * How a flagged verdict is fixed: `unknown` → recover a searchable equivalent (replace it); `vague`/`ubiquitous`
 * → turn it into a screening question (it describes a trait, not a searchable skill). `searchable`/`niche` are
 * strong — nothing to fix.
 */
export function verdictElicitation(verdict: Verdict): ElicitationKind {
  if (verdict === 'unknown') return 'recover';
  if (verdict === 'vague' || verdict === 'ubiquitous') return 'question';
  return null;
}
